/**
 * App Tour Progress (HR + admin, 2026-10-07 Manak) — how much of the App Tour
 * each current employee has finished.
 * 2026-10-08 (Manak): Phone tour and Web tour are separate columns, each counted
 * with the rule the app that shows it uses; a person whose app has no tour gets
 * a "No tour" chip and is left out of the totals (rules in appOverviewData.ts).
 */
import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { DeskState } from '@/pages/dashboard/roles/ProfileDeskParts';
import {
  APP_NO_VERSION_HINT, APP_VERSION_UNKNOWN_HINT, NO_TOUR, NO_TOUR_HINT, excelRows, filterRows, lowestFirst, shortDate, tourProgressRows, tourTotals,
  useMobileApps, useTourProgress, type ProgressFilter, type TourColumn, type TourProgressRow,
} from './appOverviewData';

export function ProgressBar({ pct }: { pct: number }) {
  const tone = pct >= 100 ? 'bg-green-700' : pct >= 50 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-gray-300';
  return <span className="inline-flex items-center gap-2"><span className="h-2 w-20 overflow-hidden rounded-full bg-gray-100" aria-hidden="true"><span className={`block h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} /></span><span className="w-10 text-right tabular-nums">{pct}%</span></span>;
}

export function NoTourChip() {
  return <span title={NO_TOUR_HINT} className="inline-block rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600">{NO_TOUR}</span>;
}

/** Bar + "done / total", or the No tour chip. */
export function TourCell({ col }: { col: TourColumn | null }) {
  if (!col) return <NoTourChip />;
  return <span className="inline-flex flex-col gap-1 whitespace-nowrap"><ProgressBar pct={col.pct} /><span className="text-xs tabular-nums text-gray-600">{col.done} / {col.total} lessons</span></span>;
}

export function AppVersionCell({ row }: { row: TourProgressRow }) {
  const { app } = row;
  if (!app.key) return <span className="text-gray-600">Web only</span>;
  const version = app.version
    ? <span className={app.version.old ? 'text-amber-700' : ''}>{app.version.name}{app.version.old ? ' – old' : ''}</span>
    : <span title={app.reportsVersion ? APP_VERSION_UNKNOWN_HINT : APP_NO_VERSION_HINT} className="text-gray-500">—</span>;
  return <span className="whitespace-nowrap">{app.name.replace(/^AURA /, '')} {version}</span>;
}

const FILTERS: Array<[ProgressFilter, string]> = [['all', 'All'], ['not-started', 'Not started'], ['below-50', 'Below 50%'], ['finished', 'Finished']];

export function AppTourProgressPage() {
  const progress = useTourProgress();
  const apps = useMobileApps();
  const rows = useMemo(() => (progress.data ? lowestFirst(tourProgressRows(progress.data, apps.data ?? null)) : []), [progress.data, apps.data]);
  const totals = tourTotals(rows);
  const [f, setF] = useState<{ progress: ProgressFilter; role: string; team: string }>({ progress: 'all', role: '', team: '' });
  const roles = useMemo(() => [...new Map(rows.filter((r) => r.role).map((r) => [r.role!, r.roleLabel])).entries()].sort((a, b) => a[1].localeCompare(b[1])), [rows]);
  const teams = useMemo(() => [...new Set(rows.map((r) => r.team).filter((t): t is string => !!t))].sort(), [rows]);
  const shown = filterRows(rows, f);
  const [exporting, setExporting] = useState(false);

  const download = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const sheet = XLSX.utils.json_to_sheet(excelRows(shown));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'App Tour progress');
      XLSX.writeFile(book, `app-tour-progress-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally { setExporting(false); }
  };

  return <div className="mx-auto max-w-screen-2xl space-y-6 p-4 md:p-6">
    <header className="flex flex-wrap items-center justify-between gap-4">
      <div><h1 className="text-2xl font-semibold">App Tour Progress</h1><p className="text-gray-600">How much of the App Tour each employee has finished, on the phone and on the web. Counts only the lessons their own app shows them.</p></div>
      <button onClick={download} disabled={!progress.data || exporting} className="inline-flex items-center gap-1 rounded bg-green-800 px-4 py-2 text-white disabled:opacity-50"><Download size={16} /> Download as Excel</button>
    </header>
    <DeskState resource={progress} />
    {progress.data && <>
      <div aria-label="App Tour totals" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {([['Employees with a tour', totals.employees], ['Started the tour', totals.started], ['Finished 100%', totals.finished], ['Not started', totals.notStarted]] as const).map(([label, n]) =>
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4"><span className="text-sm text-gray-600">{label}</span><strong className="mt-2 block text-2xl">{n}</strong></div>)}
      </div>
      {totals.noTour > 0 && <p className="text-sm text-gray-600">No tour for {totals.noTour} {totals.noTour === 1 ? 'person' : 'people'}: their app has no App Tour. Not counted above. Filters count phone and web together.</p>}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([key, label]) => <button key={key} aria-pressed={f.progress === key} onClick={() => setF((s) => ({ ...s, progress: key }))} className={`rounded-full border px-3 py-1 text-sm ${f.progress === key ? 'border-green-800 bg-green-800 text-white' : 'bg-white'}`}>{label}</button>)}
        <select aria-label="Role" value={f.role} onChange={(e) => setF((s) => ({ ...s, role: e.target.value }))} className="rounded border bg-white px-2 py-1 text-sm"><option value="">All roles</option>{roles.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select aria-label="Team" value={f.team} onChange={(e) => setF((s) => ({ ...s, team: e.target.value }))} className="rounded border bg-white px-2 py-1 text-sm"><option value="">All teams</option>{teams.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <span className="text-sm text-gray-600">{shown.length} of {rows.length}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600"><tr><th className="p-3">Employee</th><th className="p-3">Role</th><th className="p-3">Team</th><th className="p-3">Phone tour</th><th className="p-3">Web tour</th><th className="p-3">Last watched</th><th className="p-3">App</th></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-4 text-gray-600">Nobody matches these filters.</td></tr>}
            {shown.map((r) => <tr key={r.userId} className="border-t border-gray-100">
              <td className="p-3 font-medium">{r.name}</td>
              <td className="p-3">{r.roleLabel}</td>
              <td className="p-3">{r.team ?? '—'}</td>
              <td className="p-3"><TourCell col={r.phone} /></td>
              <td className="p-3"><TourCell col={r.web} /></td>
              <td className="p-3">{r.last ? <span title={r.last.lessonId}>{shortDate(r.last.doneAt)}{r.last.doneAt ? ' · ' : ''}{r.last.title}</span> : '—'}</td>
              <td className="p-3"><AppVersionCell row={r} /></td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </>}
  </div>;
}
